import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PageModal from "UIPalette/PageModal/PageModal";
import { ReactComponent as CrossInCircleIcon } from "shared/assets/cross-in-circle.svg";
import { ReactComponent as LowMoodLeftIcon } from "shared/assets/animation-icons/lowMoodLeftIcon.svg";
import { ReactComponent as LowMoodRightIcon } from "shared/assets/animation-icons/lowMoodRightIcon.svg";
import { ReactComponent as LowMoodRainIcon } from "shared/assets/animation-icons/lowMoodRainIcon.svg";
import { ReactComponent as NeutralMoodLeftIcon } from "shared/assets/animation-icons/neutralMoodLeftIcon.svg";
import { ReactComponent as NeutralMoodRightIcon } from "shared/assets/animation-icons/neutralMoodRightIcon.svg";
import { ReactComponent as HighMoodLeftIcon } from "shared/assets/animation-icons/highMoodLeftIcon.svg";
import { ReactComponent as HighMoodRightIcon } from "shared/assets/animation-icons/highMoodRightIcon.svg";
import { EventNames } from "core/monitoring/types/enums";
import { trackEvents } from "core/monitoring/Events";
import { MoodActionType } from "shared/request/myHealthyAdvantageApi";

interface MoodJourneyPageModalProps {
  mood: MoodJourneyModalTypes;
  visible: boolean;
  handleClose: () => void;
  onContinue?: () => void;
}

const moodVariants = {
  [MoodActionType.LowPositiveYouPrompt]: {
    LeftImage: LowMoodLeftIcon,
    RightImage: LowMoodRightIcon,
  },
  [MoodActionType.MidPositiveYouPrompt]: {
    LeftImage: NeutralMoodLeftIcon,
    RightImage: NeutralMoodRightIcon,
  },
  [MoodActionType.GoodPositiveYouPrompt]: {
    LeftImage: HighMoodLeftIcon,
    RightImage: HighMoodRightIcon,
  },
};

type MoodAnimationConfig = {
  left: { startX: number; endX: number; y: number; scale: number };
  right: { startX: number; endX: number; y: number; scale: number };
};

export type MoodJourneyModalTypes = MoodActionType.LowPositiveYouPrompt | MoodActionType.MidPositiveYouPrompt | MoodActionType.GoodPositiveYouPrompt;

export const moodTranslationKeys: Record<MoodJourneyModalTypes, "low" | "neutral" | "high"> = {
  [MoodActionType.LowPositiveYouPrompt]: "low",
  [MoodActionType.MidPositiveYouPrompt]: "neutral",
  [MoodActionType.GoodPositiveYouPrompt]: "high",
};

const moodAnimationConfig: Record<MoodJourneyModalTypes, MoodAnimationConfig> = {
  [MoodActionType.GoodPositiveYouPrompt]: {
    left: { startX: -120, endX: -30, y: 20, scale: 1 },
    right: { startX: 120, endX: 41.8, y: -40, scale: 1.4 },
  },
  [MoodActionType.MidPositiveYouPrompt]: {
    left: { startX: -120, endX: 0, y: 0, scale: 1.4 },
    right: { startX: 120, endX: 41.8, y: 0, scale: 1 },
  },
  [MoodActionType.LowPositiveYouPrompt]: {
    left: { startX: -120, endX: 0, y: 0, scale: 1.4 },
    right: { startX: 120, endX: 41.8, y: 0, scale: 1 },
  },
};

const getMoodPageModalVariants = (mood: MoodJourneyModalTypes) => {
  const config = moodAnimationConfig[mood];

  return {
    left: {
      initial: {
        x: config.left.startX,
        y: config.left.y,
        scale: config.left.scale,
        opacity: 1,
      },
      animate: {
        x: config.left.endX,
        y: config.left.y,
        scale: config.left.scale,
        opacity: 1,
        transition: {
          duration: 0.5,
          ease: "easeOut",
        },
      },
    },
    right: {
      initial: {
        x: config.right.startX,
        y: config.right.y,
        scale: config.right.scale,
        opacity: 1,
      },
      animate: {
        x: config.right.endX,
        y: config.right.y,
        scale: config.right.scale,
        opacity: 1,
        transition: {
          duration: 0.5,
          ease: "easeOut",
        },
      },
    },
  };
};

export const MoodJourneyPageModal = ({ mood, visible, handleClose, onContinue }: MoodJourneyPageModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const translationKey = moodTranslationKeys[mood];
  const { LeftImage, RightImage } = moodVariants[mood];
  const moodPageModalVariants = getMoodPageModalVariants(mood);

  const handleDismiss = () => {
    trackEvents(EventNames.MOOD_JOURNEY_MODAL_DISMISSED);
    handleClose();
  };

  if (!visible) {
    return null;
  }

  return (
    <PageModal
      showMobileIcon={false}
      onClose={handleDismiss}
      backIcon={CrossInCircleIcon}
      backTitle={t("moodJourney.cta.closeButton")}
      primaryCta={{
        title: t("moodJourney.cta.reflectNow"),
        onClick: () => {
          handleClose();
          navigate("/positive-you-journal");
          onContinue?.();
        },
      }}
      textCta={{
        title: t("moodJourney.cta.maybeLater"),
        onClick: handleDismiss,
      }}
    >
      <div className="flex flex-col items-center text-center gap-8">
        <div data-testid={`${mood}-illustration`} className="relative flex items-center justify-center">
          {mood === MoodActionType.LowPositiveYouPrompt && (
            <motion.div
              className="absolute inset-0 z-10 flex items-center justify-center"
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 80, opacity: 1 }}
              transition={{
                duration: 0.6,
                ease: "easeOut",
              }}
            >
              <LowMoodRainIcon />
            </motion.div>
          )}
          <motion.div
            className="absolute inset-0 z-20 flex items-center justify-center"
            variants={moodPageModalVariants.left}
            initial="initial"
            animate="animate"
          >
            <LeftImage />
          </motion.div>
          <motion.div
            className="absolute inset-0 z-0 flex items-center justify-center"
            variants={moodPageModalVariants.right}
            initial="initial"
            animate="animate"
          >
            <RightImage />
          </motion.div>
          <div className="h-[160px] w-[160px]" />
        </div>
        <div className="space-y-2">
          <h1>{t(`moodJourney.${translationKey}.title`)}</h1>
          <p>{t(`moodJourney.${translationKey}.description`)}</p>
        </div>
      </div>
    </PageModal>
  );
};
